import { AnimatePresence, motion, useReducedMotion, type Target, type TargetAndTransition } from 'framer-motion'
import React, { useMemo } from 'react'
import { UserProfile } from 'src/types'

import { edgeGeometry, geometry, cardVariants, STAGE_HEIGHT, type Role } from './geometry'
import IdeaCard from './IdeaCard'
import NavButton from './NavButton'
import type { CarouselCardModel, VisibleCard } from './types'

const ROLE_BY_OFFSET: Record<number, Role> = {
  [-2]: 'tertiaryLeft',
  [-1]: 'secondaryLeft',
  0: 'main',
  1: 'secondaryRight',
  2: 'tertiaryRight',
}

const mod = (n: number, m: number) => ((n % m) + m) % m

export default function IdeaStage({
  ideas,
  centerIndex,
  onOpenIdea,
  onNavigate,
  userData,
}: {
  ideas: CarouselCardModel[]
  centerIndex: number
  onOpenIdea?: (idea: CarouselCardModel) => void
  onNavigate: (direction: 1 | -1) => void
  userData?: UserProfile
}) {
  const reduceMotion = !!useReducedMotion()
  const count = ideas.length

  const visibleCards: VisibleCard[] = useMemo(() => {
    if (count === 0) return []
    return [-2, -1, 0, 1, 2].map(offset => {
      const idea = ideas[mod(centerIndex + offset, count)]
      return { ...idea, role: ROLE_BY_OFFSET[offset] }
    })
  }, [centerIndex, ideas, count])

  if (count === 0) return null

  return (
    <div className="">
      {/* Card stage */}
      <div className="relative" style={{ height: STAGE_HEIGHT }}>
        <AnimatePresence initial={false}>
          {visibleCards.map(card => {
            const isMain = card.role === 'main'
            const isLeftSide = card.role === 'secondaryLeft' || card.role === 'tertiaryLeft'
            const isRightSide = card.role === 'secondaryRight' || card.role === 'tertiaryRight'
            const isSide = isLeftSide || isRightSide

            const initialState: Target | Role =
              card.role === 'tertiaryRight'
                ? edgeGeometry('right')
                : card.role === 'tertiaryLeft'
                  ? edgeGeometry('left')
                  : card.role

            const exitState: TargetAndTransition | Role =
              card.role === 'tertiaryLeft'
                ? { ...geometry('tertiaryLeft'), opacity: 0, transition: { duration: 0.4, ease: 'easeInOut' } }
                : card.role === 'tertiaryRight'
                  ? edgeGeometry('right')
                  : card.role

            const activate = () => {
              if (isMain) onOpenIdea?.(card)
              else if (isLeftSide) onNavigate(-1)
              else if (isRightSide) onNavigate(1)
            }

            return (
              <motion.div
                key={card.id}
                className={[
                  'absolute rounded-3xl overflow-hidden bg-[var(--color-neutral-30)] transition-shadow duration-300',
                  isMain ? 'hover:shadow-[var(--shadow-hover)]' : '',
                ].join(' ')}
                variants={cardVariants}
                initial={initialState}
                animate={card.role}
                exit={exitState}
                transition={reduceMotion ? { duration: 0.01 } : undefined}
                onClick={activate}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    activate()
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={isMain ? `Open idea: ${card.title}` : `Show idea: ${card.title}`}
                style={{ cursor: 'pointer' }}
                whileHover={isSide ? { opacity: 0.75 } : undefined}
              >
                <IdeaCard card={card} isMain={isMain} isSide={isSide} userData={userData} />
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>

      {/* Nav buttons — centered below the cards */}
      <div className="flex items-center gap-3 justify-center">
        <NavButton direction="prev" onClick={() => onNavigate(-1)} disabled={false} />
        <NavButton direction="next" onClick={() => onNavigate(1)} disabled={false} />
      </div>
    </div>
  )
}
